import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import PageLoader from "../../components/PageLoader";
import { getCancellationProposal } from "../../api/cancellation";
import { Breakpoint } from "../../enums/Breakpoints";
import withV3TranslationsProvider from "../../i18nV3/WithV3TranslationsProvider";
import ErrorView from "../../components/CancellationPage/ErrorView";
import { CancellationProposal } from "../../types/Cancellation";
import { CancellationStatus, CancellationView } from "../../enums/Cancellation";
import AlreadyConfirmedView from "../../components/CancellationPage/AlreadyConfirmedView";
import RequestView from "../../components/CancellationPage/RequestView";
import ExpiredView from "../../components/CancellationPage/ExpiredView";

const CANCELLATION_VIEW_FROM_STATUS = {
  [CancellationStatus.Valid]: CancellationView.Request,
  [CancellationStatus.AlreadyAccepted]: CancellationView.AlreadyConfirmed,
  [CancellationStatus.Expired]: CancellationView.Expired,
};

const Cancellation = () => {
  const params = useParams<{ tripUuid: string }>();

  const [isLoading, setIsLoading] = useState(true);
  const [view, setView] = useState<CancellationView>();
  const [cancellation, setCancellation] = useState<CancellationProposal>();

  const fetchCancellation = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getCancellationProposal(params.tripUuid);
      setCancellation(data);
      setView(CANCELLATION_VIEW_FROM_STATUS[data.offerStatus]);
    } catch (error) {
      setView(CancellationView.Error);
    } finally {
      setIsLoading(false);
    }
  }, [params.tripUuid]);
  useEffect(() => {
    fetchCancellation();
  }, [fetchCancellation]);

  const currentView = useMemo(() => {
    if (!view) return null;
    if (cancellation) {
      switch (view) {
        case CancellationView.Request: {
          return <RequestView cancellationProposal={cancellation} />;
        }
        case CancellationView.AlreadyConfirmed: {
          return <AlreadyConfirmedView cancellationProposal={cancellation} />;
        }
        case CancellationView.Expired: {
          return <ExpiredView />;
        }
      }
    }
    return <ErrorView />;
  }, [cancellation, view]);

  if (isLoading) {
    return <PageLoader />;
  }
  return <Container>{currentView}</Container>;
};

const Container = styled.div`
  padding: 80px 20px;
  max-width: 728px;

  @media ${Breakpoint.MinTablet} {
    padding: 100px 0;
    margin: 0 auto;
  }
`;

export default withV3TranslationsProvider(Cancellation);
